import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ExternalLink, Loader2, Plus } from 'lucide-react';
import {
  addDays, addMinutes, addWeeks, format, isSameDay, isValid, parse, startOfDay, startOfWeek,
} from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { OwnerChip } from '../kit/OwnerChip';
import { fromApi } from '../kit/time';
import StatusSelect from './StatusSelect';

// Calendar week grid (ticket 143 P3). Layout over the same GET /v2/appointments
// rows as the list. Edits go through the page's handlers, all GHL-first: status
// (P2), and in P4 create + reschedule. Desktop drags a block to a new time and
// clicks an empty slot to book; everywhere the toolbar "New" button and the
// popover's "Reschedule" button open the dialog. The grid never moves a block
// itself — it only reports the drop; the page moves the block at once (ticket
// 189), labels it "Moving…" while GHL is asked (ticket 216), and puts it back
// if GHL refuses.
//
// Everything is in the viewer's local zone: weeks run Sunday–Saturday from
// local midnight, and a block's position is its local wall-clock time.

export const HOUR_PX = 48;
const FIRST_HOUR = 7; // 7am–8pm is what's on screen before scrolling
const LAST_HOUR = 20;
const DEFAULT_MINUTES = 30; // no end time from GHL
const MIN_MINUTES = 30; // shortest block drawn, so a 15-minute slot is still clickable
const HEAD_PX = 56; // sticky day-name header inside the scroller
const WEEK = { weekStartsOn: 0 };
const CLICK_STEP = 30; // empty-slot click snaps down to the half hour
const DRAG_STEP = 15; // drop target snaps to the quarter hour
const DRAG_PX = 4; // less movement than this is a click, not a drag

// ── week math (pure, exported for tests) ─────────────────────────────────

// "YYYY-MM-DD" (any day of the week) → that week's Sunday, local midnight.
// Missing or bad → this week.
export function parseWeek(key, today = new Date()) {
  const d = key && /^\d{4}-\d{2}-\d{2}$/.test(key) ? parse(key, 'yyyy-MM-dd', today) : today;
  return startOfWeek(isValid(d) ? d : today, WEEK);
}

export const weekKey = (d) => format(startOfWeek(d, WEEK), 'yyyy-MM-dd');

// addDays steps calendar days, so a 23- or 25-hour DST day stays one column.
export const weekDays = (start) => Array.from({ length: 7 }, (_, i) => addDays(start, i));

// Query window for GET /v2/appointments: [Sunday 00:00, next Sunday 00:00) local,
// opened a day early — the API matches on start time, and a Saturday-night
// appointment running past midnight still belongs on this week's Sunday.
// daySegments drops whatever falls outside each drawn day.
export const weekRange = (start) => ({ from: addDays(start, -1).toISOString(), to: addDays(start, 7).toISOString() });

const wall = (d) => d.getHours() * 60 + d.getMinutes();

// Wall-clock minutes → a multiple of `step`: down by default, nearest with `round`.
export const snapMinutes = (min, step, round = false) => (round ? Math.round(min / step) : Math.floor(min / step)) * step;

// A y offset inside a day column (px from local midnight) → that day's local
// Date at the snapped time. Built from local fields, not "midnight + N ms", so
// a 23- or 25-hour DST day still lands on the wall-clock time you pointed at.
export function dropStart(day, offsetY, step, round = false) {
  const min = Math.min(24 * 60 - step, Math.max(0, snapMinutes((offsetY / HOUR_PX) * 60, step, round)));
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), Math.floor(min / 60), min % 60);
}

// The part of each appointment that falls on `day`, as local wall-clock
// minutes [top, bottom). Appointments crossing midnight are clipped.
export function daySegments(appts, day) {
  const dayStart = startOfDay(day);
  const dayEnd = addDays(dayStart, 1);
  const out = [];
  appts.forEach((a) => {
    const start = fromApi(a.start_at);
    if (!isValid(start)) return;
    let end = a.end_at ? fromApi(a.end_at) : null;
    if (!end || !isValid(end) || end <= start) end = addMinutes(start, DEFAULT_MINUTES);
    if (start >= dayEnd || end <= dayStart) return;
    const top = start < dayStart ? 0 : wall(start);
    const bottom = end >= dayEnd ? 24 * 60 : wall(end);
    out.push({ a, start, continued: start < dayStart, top, bottom: Math.min(24 * 60, Math.max(bottom, top + MIN_MINUTES)) });
  });
  return out;
}

// Side-by-side layout for overlaps. Each run of transitively overlapping
// blocks shares one set of columns; a block takes the first free column.
// Returns segments with `col` and `cols` (column count of its run).
export function layoutDay(segs) {
  const sorted = [...segs].sort((x, y) => (
    x.top - y.top
    || (y.bottom - y.top) - (x.bottom - x.top)
    || String(x.a.appointment_id).localeCompare(String(y.a.appointment_id))
  ));
  const placed = [];
  let run = [];
  let colEnds = [];
  let runEnd = -1;
  const close = () => {
    run.forEach((p) => { p.cols = colEnds.length; });
    run = [];
    colEnds = [];
  };
  sorted.forEach((s) => {
    if (s.top >= runEnd) close();
    let col = colEnds.findIndex((end) => end <= s.top);
    if (col === -1) {
      col = colEnds.length;
      colEnds.push(s.bottom);
    } else {
      colEnds[col] = s.bottom;
    }
    const p = { ...s, col };
    run.push(p);
    placed.push(p);
    runEnd = Math.max(runEnd, s.bottom);
  });
  close();
  return placed;
}

// ── hooks ────────────────────────────────────────────────────────────────

const NARROW = '(max-width: 767px)';

function useNarrow() {
  const get = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(NARROW).matches;
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const m = window.matchMedia(NARROW);
    const on = () => setNarrow(m.matches);
    m.addEventListener?.('change', on);
    return () => m.removeEventListener?.('change', on);
  }, []);
  return narrow;
}

function useNow() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

// ── view ─────────────────────────────────────────────────────────────────

const hourLabel = (h) => (h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`);

const BLOCK_STYLE = {
  confirmed: 'bg-indigo-50 border-indigo-500 text-indigo-900',
  showed: 'bg-sky-50 border-sky-500 text-sky-900',
  noshow: 'bg-amber-50 border-amber-500 text-amber-900',
  cancelled: 'bg-slate-50 border-slate-300 text-slate-400 line-through opacity-70',
};

function whenText(a) {
  const start = fromApi(a.start_at);
  if (!isValid(start)) return '—';
  const end = a.end_at ? fromApi(a.end_at) : null;
  const day = format(start, 'EEE, MMM d');
  return end && isValid(end) && end > start
    ? `${day} · ${format(start, 'h:mm a')}–${format(end, 'h:mm a')}`
    : `${day} · ${format(start, 'h:mm a')}`;
}

function AppointmentBlock({ seg, staff, open, onOpenChange, savingIds, onStatusChange, onDragStart, onSuppressedClick, dragging, onRescheduleOpen, onEditDetails }) {
  const { a, start, top, bottom, col, cols, continued } = seg;
  const id = a.appointment_id;
  const status = (a.status || '').toLowerCase();
  const cancelled = status === 'cancelled';
  const editable = a.source === 'ghl' && !cancelled;
  const savingLabel = savingIds?.get?.(id) || null; // "Moving…" / "Saving…" while GHL is asked
  const saving = !!savingLabel;
  // Not while its own save is in flight, and not the clipped second-day piece
  // of a midnight-crossing block (a click still opens the popover).
  const draggable = editable && !!onDragStart && !saving && !continued;
  const height = Math.max(((bottom - top) / 60) * HOUR_PX - 2, 16);
  const title = a.title || 'Appointment';
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`week-appt-${id}`}
          data-col={col}
          data-cols={cols}
          aria-label={`${title}, ${whenText(a)}${cancelled ? ', cancelled' : ''}`}
          data-appt-block=""
          onPointerDown={draggable ? (e) => onDragStart(seg, e) : undefined}
          onClickCapture={onSuppressedClick}
          className={`absolute overflow-hidden rounded-md border-l-[3px] px-1.5 py-0.5 text-left text-[11px] leading-tight shadow-sm hover:z-10 hover:shadow focus:z-10 focus:outline-none focus:ring-2 focus:ring-brand-accent ${BLOCK_STYLE[status] || BLOCK_STYLE.confirmed} ${draggable ? 'cursor-grab' : ''} ${dragging ? 'opacity-40' : ''} ${saving ? 'border border-dashed opacity-70' : ''}`}
          style={{
            top: (top / 60) * HOUR_PX,
            height,
            left: `calc(${(col / cols) * 100}% + 2px)`,
            width: `calc(${100 / cols}% - 4px)`,
          }}
        >
          {saving && <Loader2 className="absolute right-1 top-1 animate-spin text-slate-500" size={12} data-testid={`week-saving-${id}`} />}
          {height < 36 ? (
            <span className="block truncate"><span className="font-semibold">{title}</span> {format(start, 'h:mm a')}{saving && <span className="italic"> · {savingLabel}</span>}</span>
          ) : (
            <>
              <span className="block truncate font-semibold">{title}</span>
              <span className="block truncate">{format(start, 'h:mm a')}{saving && <span className="italic"> · {savingLabel}</span>}</span>
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-3 text-sm" data-testid={`week-popover-${id}`}>
        <div>
          <p className={`font-semibold ${cancelled ? 'line-through text-slate-400' : 'text-brand-navy'}`}>{title}</p>
          <p className="text-xs text-slate-500">{whenText(a)}</p>
          {a.calendar_name && <p className="text-xs text-slate-500">{a.calendar_name}</p>}
        </div>
        <dl className="grid grid-cols-[4.5rem_1fr] items-center gap-x-2 gap-y-2 text-xs">
          <dt className="text-slate-500">Contact</dt>
          <dd>
            {a.contact_id ? (
              <Link to={`/portal/contacts/${a.contact_id}`} className="text-brand-navy underline" data-testid={`week-contact-${id}`}>
                {a.contact_name || 'View contact'}
              </Link>
            ) : '—'}
          </dd>
          <dt className="text-slate-500">Owner</dt>
          <dd><OwnerChip userId={a.owner_id} name={a.owner_name} staff={staff} showName /></dd>
          {a.address && (
            <>
              <dt className="text-slate-500">Address</dt>
              <dd className="break-words" data-testid={`week-address-${id}`}>{a.address}</dd>
            </>
          )}
          {a.notes && (
            <>
              <dt className="self-start text-slate-500">Notes</dt>
              <dd className="max-h-24 overflow-y-auto whitespace-pre-wrap break-words" data-testid={`week-notes-${id}`}>{a.notes}</dd>
            </>
          )}
          <dt className="text-slate-500">Status</dt>
          <dd>
            <StatusSelect
              appointment={a}
              saving={saving}
              onChange={onStatusChange}
              testId={`week-status-${id}`}
            />
          </dd>
        </dl>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-2">
            {editable && onRescheduleOpen && (
              <button
                type="button"
                disabled={saving}
                onClick={() => { onOpenChange(false); onRescheduleOpen(a); }}
                className="h-7 rounded-md border border-slate-200 px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                data-testid={`week-reschedule-${id}`}
              >
                Reschedule
              </button>
            )}
            {a.source === 'ghl' && onEditDetails && (
              <button
                type="button"
                disabled={saving}
                onClick={() => { onOpenChange(false); onEditDetails(a); }}
                className="h-7 rounded-md border border-slate-200 px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                data-testid={`week-edit-details-${id}`}
              >
                Edit details
              </button>
            )}
          </div>
          {a.ghl_edit_url && (
            <a href={a.ghl_edit_url} target="_blank" rel="noopener noreferrer"
              className="text-xs inline-flex items-center gap-0.5 text-indigo-700 underline" data-testid={`week-open-ghl-${id}`}>
              Open in GHL <ExternalLink size={11} />
            </a>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function WeekGrid({
  week, rows, loading, error, onRetry, onWeekChange, staff, savingIds, onStatusChange, onCreate, onReschedule, onRescheduleOpen, onEditDetails,
}) {
  const narrow = useNarrow();
  const now = useNow();
  const start = useMemo(() => parseWeek(week), [week]);
  const days = useMemo(() => weekDays(start), [start]);
  const todayIdx = days.findIndex((d) => isSameDay(d, now));
  const [dayIdx, setDayIdx] = useState(Math.max(todayIdx, 0));
  const [openId, setOpenId] = useState(null);
  const scroller = useRef(null);
  const dayEls = useRef([]);
  const [drag, setDrag] = useState(null); // { id, dayIdx, start, height } while a block is being dragged
  const dragRef = useRef(null); // live drag bookkeeping (listeners, cancelled flag)
  const swallowClick = useRef(false); // the click that ends a drag must not open anything
  const wasOpen = useRef(false); // a popover was open when this press began

  // New week: phone view lands on today if it's in the week, else Sunday.
  useEffect(() => {
    setDayIdx(Math.max(days.findIndex((d) => isSameDay(d, new Date())), 0));
    setOpenId(null);
  }, [days]);

  // Unmount mid-drag: drop the window listeners.
  useEffect(() => () => dragRef.current?.stop(), []);

  useLayoutEffect(() => {
    if (scroller.current) scroller.current.scrollTop = FIRST_HOUR * HOUR_PX - 8;
  }, [week, narrow]);

  const shown = useMemo(() => (narrow ? [days[dayIdx]] : days), [narrow, dayIdx, days]);
  const columns = useMemo(() => shown.map((d) => layoutDay(daySegments(rows || [], d))), [shown, rows]);
  const end = days[6];
  const label = start.getFullYear() === end.getFullYear()
    ? `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`
    : `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
  const gridCols = { gridTemplateColumns: `3.5rem repeat(${shown.length}, minmax(0, 1fr))` };
  const hours = Array.from({ length: 24 }, (_, h) => h);

  // Which day column is under x, and the y offset inside it. Outside the grid
  // it clamps to the nearest column.
  const targetAt = (x, y, grabPx) => {
    const els = dayEls.current.slice(0, shown.length);
    let idx = els.findIndex((el) => el && x < el.getBoundingClientRect().right);
    if (idx === -1) idx = els.length - 1;
    const rect = els[idx].getBoundingClientRect();
    return { idx, offsetY: y - rect.top - grabPx };
  };

  // Desktop drag of a GHL, non-cancelled block. Under DRAG_PX it stays a click
  // (the popover opens as before); past it a ghost follows the pointer and the
  // drop reports onReschedule. Escape cancels. Mouse / pen only — a finger on a
  // touch screen scrolls, and phones have the Reschedule button.
  const startDrag = (seg, e) => {
    if (narrow || !onReschedule || e.button !== 0 || e.pointerType === 'touch') return;
    dragRef.current?.stop();
    const rect = e.currentTarget.getBoundingClientRect();
    const st = {
      x0: e.clientX, y0: e.clientY, grabPx: e.clientY - rect.top,
      active: false, cancelled: false, target: null,
      height: Math.max(((seg.bottom - seg.top) / 60) * HOUR_PX - 2, 16),
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('keydown', key);
      if (dragRef.current === st) dragRef.current = null;
    };
    function move(ev) {
      if (st.cancelled) return;
      if (!st.active && Math.hypot(ev.clientX - st.x0, ev.clientY - st.y0) < DRAG_PX) return;
      if (!st.active) { st.active = true; setOpenId(null); }
      const { idx, offsetY } = targetAt(ev.clientX, ev.clientY, st.grabPx);
      st.target = { dayIdx: idx, start: dropStart(shown[idx], offsetY, DRAG_STEP, true) };
      setDrag({ id: seg.a.appointment_id, height: st.height, ...st.target });
    }
    function up() {
      stop();
      setDrag(null);
      if (!st.active) return;
      swallowClick.current = true;
      setTimeout(() => { swallowClick.current = false; }, 0);
      if (!st.cancelled && st.target && st.target.start.getTime() !== seg.start.getTime()) onReschedule(seg.a, st.target.start);
    }
    function key(ev) {
      if (ev.key !== 'Escape') return;
      st.cancelled = true;
      setDrag(null);
    }
    st.stop = stop;
    dragRef.current = st;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('keydown', key);
  };

  const eatDragClick = (e) => {
    if (!swallowClick.current) return;
    e.preventDefault();
    e.stopPropagation();
  };

  // Empty space in a day column → book at that (half-hour) time. Blocks, the
  // popover (portalled, but React bubbles through portals) and the click that
  // closes an open popover are not empty space.
  const clickDay = (d) => (e) => {
    if (narrow || !onCreate || swallowClick.current || wasOpen.current) return;
    if (!e.currentTarget.contains(e.target) || e.target.closest('[data-appt-block]')) return;
    onCreate(dropStart(d, e.clientY - e.currentTarget.getBoundingClientRect().top, CLICK_STEP));
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm" data-testid="week-grid">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-100">
        <button type="button" onClick={() => onWeekChange(weekKey(new Date()))}
          className="h-8 px-3 rounded-md border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="week-today">
          Today
        </button>
        <button type="button" aria-label="Previous week" onClick={() => onWeekChange(weekKey(addWeeks(start, -1)))}
          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100" data-testid="week-prev">
          <ChevronLeft size={18} />
        </button>
        <button type="button" aria-label="Next week" onClick={() => onWeekChange(weekKey(addWeeks(start, 1)))}
          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100" data-testid="week-next">
          <ChevronRight size={18} />
        </button>
        {onCreate && (
          <button type="button" onClick={() => onCreate(null)}
            className="h-8 px-3 inline-flex items-center gap-1 rounded-md bg-brand-navy text-sm font-medium text-white hover:bg-brand-navy-light" data-testid="week-new">
            <Plus size={14} /> New
          </button>
        )}
        <h2 className="text-sm font-semibold text-brand-navy" data-testid="week-label">{label}</h2>
        {loading && <Loader2 className="animate-spin text-slate-400" size={16} data-testid="week-loading" />}
        {!loading && !error && rows && rows.length === 0 && (
          <span className="text-xs text-slate-400" data-testid="week-empty">No appointments this week.</span>
        )}
      </div>

      {error && (
        <div role="alert" className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-red-100 bg-red-50 text-sm text-red-700" data-testid="week-error">
          <span>Couldn’t load this week’s appointments. {error}</span>
          <button type="button" onClick={onRetry}
            className="h-7 px-2.5 rounded-md border border-red-200 bg-white text-xs font-medium text-red-700 hover:bg-red-100" data-testid="week-retry">
            Retry
          </button>
        </div>
      )}

      {narrow && (
        <div className="grid grid-cols-7 gap-1 px-2 py-2 border-b border-slate-100" role="tablist" data-testid="week-daypicker">
          {days.map((d, i) => (
            <button
              key={d.toISOString()}
              type="button"
              role="tab"
              aria-selected={i === dayIdx}
              onClick={() => setDayIdx(i)}
              data-testid={`week-pick-${format(d, 'yyyy-MM-dd')}`}
              className={`flex flex-col items-center rounded-lg py-1 text-xs ${i === dayIdx ? 'bg-brand-navy text-white' : i === todayIdx ? 'text-brand-accent font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              <span>{format(d, 'EEEEE')}</span>
              <span className="text-sm font-semibold">{format(d, 'd')}</span>
            </button>
          ))}
        </div>
      )}

      <div ref={scroller} className="overflow-y-auto" style={{ height: (LAST_HOUR - FIRST_HOUR) * HOUR_PX + HEAD_PX + 16, maxHeight: narrow ? 'calc(100dvh - 13rem)' : undefined }} data-testid="week-scroller">
        {/* Phones: the day picker already names the day. */}
        <div className={`sticky top-0 z-20 grid bg-white border-b border-slate-100 ${narrow ? 'hidden' : ''}`} style={{ ...gridCols, height: HEAD_PX }}>
          <div />
          {shown.map((d) => {
            const isToday = isSameDay(d, now);
            return (
              <div key={d.toISOString()} className="py-1.5 text-center border-l border-slate-100" data-testid={`week-head-${format(d, 'yyyy-MM-dd')}`}>
                <span className="block text-[11px] uppercase tracking-wide text-slate-500">{format(d, 'EEE')}</span>
                <span className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-sm font-semibold ${isToday ? 'bg-brand-accent text-white' : 'text-brand-navy'}`}>
                  {format(d, 'd')}
                </span>
              </div>
            );
          })}
        </div>
        <div className="relative grid" style={{ ...gridCols, height: 24 * HOUR_PX }}>
          <div className="relative">
            {hours.map((h) => (
              <span key={h} className="absolute right-2 -translate-y-1/2 text-[10px] text-slate-400" style={{ top: h * HOUR_PX }}>
                {h === 0 ? '' : hourLabel(h)}
              </span>
            ))}
          </div>
          {shown.map((d, i) => (
            <div
              key={d.toISOString()}
              ref={(el) => { dayEls.current[i] = el; }}
              className={`relative border-l border-slate-100 ${drag ? 'select-none' : ''}`}
              data-testid={`week-day-${format(d, 'yyyy-MM-dd')}`}
              onPointerDownCapture={() => { wasOpen.current = openId !== null; }}
              onClick={clickDay(d)}
            >
              {hours.map((h) => (
                <div key={h} className="absolute inset-x-0 border-t border-slate-100" style={{ top: h * HOUR_PX }} />
              ))}
              {columns[i].map((seg) => (
                <AppointmentBlock
                  key={seg.a.appointment_id}
                  seg={seg}
                  staff={staff}
                  open={openId === seg.a.appointment_id}
                  onOpenChange={(o) => setOpenId(o ? seg.a.appointment_id : null)}
                  savingIds={savingIds}
                  onStatusChange={onStatusChange}
                  onDragStart={narrow ? null : startDrag}
                  onSuppressedClick={eatDragClick}
                  dragging={drag?.id === seg.a.appointment_id}
                  onRescheduleOpen={onRescheduleOpen}
                  onEditDetails={onEditDetails}
                />
              ))}
              {drag && drag.dayIdx === i && (
                <div
                  className="pointer-events-none absolute inset-x-0.5 z-30 rounded-md border-2 border-dashed border-brand-accent bg-brand-accent/10 px-1.5 py-0.5 text-[11px] font-semibold text-brand-navy"
                  style={{ top: (wall(drag.start) / 60) * HOUR_PX, height: drag.height }}
                  data-testid="week-drag-ghost"
                >
                  {format(drag.start, 'h:mm a')}
                </div>
              )}
              {isSameDay(d, now) && (
                <div className="pointer-events-none absolute inset-x-0 z-10 flex items-center" style={{ top: (wall(now) / 60) * HOUR_PX }} data-testid="week-now-line">
                  <span className="-ml-1 h-2 w-2 rounded-full bg-red-500" />
                  <span className="h-px flex-1 bg-red-500" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
